import { useCallback, useRef, useState } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent, ScrollView } from 'react-native';

import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { useActiveChallengesBadge } from '../../hooks/use-active-challenges-badge';
import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useChallengesUi, useLoyaltyIncentives } from '../../queries/loyalty.queries';

export type Section = 'challenges' | 'offers' | 'rewards';

export const useScrollManager = () => {
  const [activeSection, setActiveSection] = useState<Section>('challenges');
  const scrollViewRef = useRef<ScrollView | null>(null);
  const targetScrollY = useRef<number>();
  const [sectionPositions, setSectionPositions] = useState<Partial<Record<Section, number>>>({});

  const attachScrollView = useCallback((scrollView: ScrollView | null) => {
    scrollViewRef.current = scrollView;
    targetScrollY.current = undefined;
    if (scrollView) {
      // Refreshes recreate the ScrollView at the top while this hook stays mounted.
      setActiveSection('challenges');
    }
  }, []);

  const scrollToSection = useCallback(
    (sectionName: Section, animated = true) => {
      const position = sectionPositions[sectionName];
      if (position !== undefined && scrollViewRef.current) {
        setActiveSection(sectionName);
        targetScrollY.current = position;
        scrollViewRef.current.scrollTo({ y: position, animated });
        return true;
      }
      return false;
    },
    [sectionPositions]
  );

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (targetScrollY.current !== undefined) {
        const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
        const maxScrollY = Math.max(0, contentSize.height - layoutMeasurement.height);
        const targetY = Math.min(targetScrollY.current, maxScrollY);
        if (Math.abs(contentOffset.y - targetY) <= 1) {
          targetScrollY.current = undefined;
        }
        // Keep the tapped tab highlighted while passing through other sections.
        return;
      }

      const scrollY = event.nativeEvent.contentOffset.y + 40;

      if (sectionPositions.rewards !== undefined && scrollY >= sectionPositions.rewards) {
        setActiveSection('rewards');
      } else if (sectionPositions.offers !== undefined && scrollY >= sectionPositions.offers) {
        setActiveSection('offers');
      } else {
        setActiveSection('challenges');
      }
    },
    [sectionPositions]
  );

  const onScrollBeginDrag = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      targetScrollY.current = undefined;
      onScroll(event);
    },
    [onScroll]
  );

  const setSectionPosition = useCallback((sectionName: Section, y: number) => {
    setSectionPositions(positions => ({ ...positions, [sectionName]: y }));
  }, []);

  return {
    scrollViewRef: attachScrollView,
    activeSection,
    scrollToSection,
    onScroll,
    onScrollBeginDrag,
    setSectionPosition,
    sectionPositions,
  };
};

export const useIsLoyaltyPageLoading = () => {
  const { loyaltyUserReady } = useLoyaltyLegacyStates();

  const { isLoading: isChallengesLoading } = useChallengesUi();
  const { isLoading: isLoyaltyIncentivesLoading } = useLoyaltyIncentives();

  return isLoyaltyIncentivesLoading || !loyaltyUserReady || isChallengesLoading;
};

export const useShouldHideChallenges = () => {
  const { isAuthenticated } = useAuthContext();
  const isChallengeUiEnabled = useFlag('enable-challenge-ui');
  const { data: challengesData, isPending } = useChallengesUi();

  return (
    !isChallengeUiEnabled ||
    !isAuthenticated ||
    (challengesData && challengesData.length === 0) ||
    (!isPending && !challengesData)
  );
};

export const useShouldShowSectionsHeader = () => {
  const isDesktop = useIsDesktopBp();

  const shouldHideChallenges = useShouldHideChallenges();

  const isLoading = useIsLoyaltyPageLoading();

  return !isDesktop && !shouldHideChallenges && !isLoading;
};

export const useSections = (activeSection: Section, onPress: (section: Section) => void) => {
  const activeChallengesBadge = useActiveChallengesBadge();
  return [
    {
      id: 'challenges',
      title: 'Challenges',
      onPress: () => onPress('challenges'),
      isActive: activeSection === 'challenges',
      badge: activeChallengesBadge,
    },
    {
      id: 'offers',
      title: 'Offers',
      onPress: () => onPress('offers'),
      isActive: activeSection === 'offers',
    },
    {
      id: 'rewards',
      title: 'Rewards',
      onPress: () => onPress('rewards'),
      isActive: activeSection === 'rewards',
    },
  ];
};
